import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle,
  DollarSign,
  FileText,
  Users,
  Sparkles,
  ArrowRight,
  X,
  Calendar,
  Clock,
  CreditCard,
  Wallet,
} from "lucide-react";
import Sidebar from "./Sidebar";
import { dashboardApi, tasksApi } from "../services/api";
import { useUserDataContext } from "../context/UserDataContext";
import ThemeProvider from "./ThemeProvider";
import StatCard from "./dashboard/StatCard";
import GaugeWidget from "./dashboard/GaugeWidget";
import RevenueTrendChart from "./dashboard/RevenueTrendChart";
import ArAgingChart from "./dashboard/ArAgingChart";
import TeamUtilizationChart from "./dashboard/TeamUtilizationChart";
import BillableHoursProgress from "./dashboard/BillableHoursProgress";
import TaskListWidget from "./dashboard/TaskListWidget";
import MatterListWidget from "./dashboard/MatterListWidget";
import ScheduleWidget from "./dashboard/ScheduleWidget";
import MessagesWidget from "./dashboard/MessagesWidget";
import FinancialAlertsWidget from "./dashboard/FinancialAlertsWidget";
import LeadPipelineWidget from "./dashboard/LeadPipelineWidget";
import DocumentsAwaitingWidget from "./dashboard/DocumentsAwaitingWidget";
import ClosingDatesWidget from "./dashboard/ClosingDatesWidget";
import AiSuggestionsWidget from "./dashboard/AiSuggestionsWidget";
import WorkAllocationWidget from "./dashboard/WorkAllocationWidget";
import ComplianceWidget from "./dashboard/ComplianceWidget";
import { formatCurrency, formatDate } from "./dashboard/utils";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DashboardProps {
  handleSignOut: () => void;
  customerId: string | null;
  priceId: string | null;
  subscriptionStatus: "free trial" | "basic" | "pro" | "elite";
  trialExpiresAt: number;
}

type DashboardRole = "owner" | "lawyer" | "billing" | "staff";

interface CoreData {
  tasks: { overdue: any[]; today: any[]; week: any[] };
  todayEvents: any[];
  recentMessages: any[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getTimeOfDay = (): string => {
  const h = new Date().getHours();
  if (h < 12) return "morning";
  if (h < 17) return "afternoon";
  return "evening";
};

const roleBadgeStyles: Record<DashboardRole, { bg: string; text: string; label: string }> = {
  owner: { bg: "#dcfce7", text: "#166534", label: "Firm Owner" },
  lawyer: { bg: "#dbeafe", text: "#1e40af", label: "Lawyer" },
  billing: { bg: "#f3e8ff", text: "#6b21a8", label: "Billing" },
  staff: { bg: "#f1f5f9", text: "#475569", label: "Staff" },
};

// ---------------------------------------------------------------------------
// Role Layout: Owner (3-col grid)
// ---------------------------------------------------------------------------

const OwnerDashboard: React.FC<{
  data: any;
  core: CoreData;
  onMarkDone: (taskId: string) => void;
}> = ({ data, core, onMarkDone }) => {
  const ownerData = data?.owner || {};

  // AR aging mapping: bucket -> label
  const arAgingData = (ownerData.arAging || []).map((a: any) => ({
    label: a.bucket,
    totalCents: a.totalCents,
    count: a.count,
  }));

  // New matters trend
  const thisMonth = ownerData.newMatters?.thisMonth ?? 0;
  const lastMonth = ownerData.newMatters?.lastMonth ?? 0;
  const matterTrendDirection =
    thisMonth > lastMonth ? "up" : thisMonth < lastMonth ? "down" : "flat";
  const matterTrendLabel =
    matterTrendDirection === "flat"
      ? "Same as last month"
      : `${Math.abs(thisMonth - lastMonth)} ${matterTrendDirection === "up" ? "more" : "fewer"} than last month`;

  // Lead pipeline total
  const leadPipeline: { status: string; count: number }[] = ownerData.leadPipeline || [];
  const totalLeads = leadPipeline.reduce((s: number, p: { count: number }) => s + p.count, 0);

  // Documents awaiting upload
  const docsAwaiting: any[] = ownerData.docsAwaitingUpload || [];

  // Closing dates (only present for firms with real estate)
  const closingDates: any[] = ownerData.closingDates || [];

  return (
    <div className="space-y-6">
      {/* Row 1: AR Aging (2 cols) + Collection Rate gauge (1 col) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <ArAgingChart data={arAgingData} />
        </div>
        <GaugeWidget
          label="Collection Rate"
          value={ownerData.collectionRate ?? 0}
          color="#0d6efd"
          icon={DollarSign}
          subtitle="Amount collected as a % of amount billed"
        />
      </div>

      {/* Row 2: Revenue Trend (2 cols) + Stats (1 col) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <RevenueTrendChart data={ownerData.revenueTrend || []} />
        </div>
        <div className="space-y-6">
          <StatCard
            label="New Matters"
            value={thisMonth}
            icon={FileText}
            color="#0F5132"
            trend={{ direction: matterTrendDirection as "up" | "down" | "flat", label: matterTrendLabel }}
          />
          <StatCard
            label="Leads in Pipeline"
            value={totalLeads}
            icon={Users}
            color="#198754"
          />
        </div>
      </div>

      {/* Row 3: AI Intelligence — Suggestions + Work Allocation + Compliance */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <AiSuggestionsWidget
          pendingCount={ownerData.aiSuggestions?.pendingCount ?? 0}
          totalValueCents={ownerData.aiSuggestions?.totalValueCents ?? 0}
          topSuggestions={ownerData.aiSuggestions?.topSuggestions ?? []}
        />
        <WorkAllocationWidget data={ownerData.workAllocation ?? []} />
        <ComplianceWidget activeRulesCount={ownerData.complianceScore?.activeRulesCount ?? 0} />
      </div>

      {/* Row 4: Documents Awaiting Upload (1 col) + Team Utilization (2 cols) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <DocumentsAwaitingWidget matters={docsAwaiting} />
        <div className="lg:col-span-2">
          <TeamUtilizationChart data={ownerData.teamUtilization || []} />
        </div>
      </div>

      {/* Row 5: Tasks (2 cols) + Sidebar stack (1 col) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <TaskListWidget tasks={core.tasks} onMarkDone={onMarkDone} />
        </div>
        <div className="space-y-6">
          <ScheduleWidget events={core.todayEvents} />
          <MessagesWidget messages={core.recentMessages} />
        </div>
      </div>

      {/* Row 5: Financial Alerts + Lead Pipeline */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <FinancialAlertsWidget alerts={ownerData.financialAlerts || []} />
        </div>
        <div>
          <LeadPipelineWidget pipeline={leadPipeline} />
        </div>
      </div>

      {/* Row 6: Upcoming Closings (only for firms with real estate) */}
      {closingDates.length > 0 && (
        <ClosingDatesWidget closingDates={closingDates} />
      )}

      {/* Row 7: Active Matters (full width) */}
      <MatterListWidget
        matters={ownerData.matters || []}
        title="Active Matters"
        limit={12}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Role Layout: Lawyer (2+1 grid)
// ---------------------------------------------------------------------------

const LawyerDashboard: React.FC<{
  data: any;
  core: CoreData;
  onMarkDone: (taskId: string) => void;
}> = ({ data, core, onMarkDone }) => {
  const lawyerData = data?.lawyer || {};
  const billableHours = lawyerData.billableHours || {
    today: { minutes: 0, targetMinutes: 480 },
    week: { minutes: 0, targetMinutes: 2400 },
    month: { minutes: 0, targetMinutes: 10000 },
  };
  const upcomingDeadlines: any[] = lawyerData.upcomingDeadlines || [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Main column */}
      <div className="lg:col-span-2 space-y-6">
        <BillableHoursProgress
          today={billableHours.today}
          week={billableHours.week}
          month={billableHours.month}
        />

        {/* Unbilled Time stat */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <StatCard
            label="Unbilled Time"
            value={formatCurrency(lawyerData.unbilledTimeCents ?? 0)}
            icon={Clock}
            color="#0F5132"
          />
        </div>

        <TaskListWidget tasks={core.tasks} onMarkDone={onMarkDone} />

        <MatterListWidget
          matters={lawyerData.myMatters || []}
          title="My Matters"
          showDeadline={true}
        />

        {/* Upcoming Deadlines (inline) */}
        {upcomingDeadlines.length > 0 && (
          <section className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="h-5 w-5 text-slate-700" aria-hidden="true" />
              <h2 className="text-lg font-bold" style={{ color: "#1F2937" }}>
                Upcoming Deadlines
              </h2>
            </div>
            <div className="space-y-3">
              {upcomingDeadlines.map((dl: any, idx: number) => (
                <div
                  key={dl._id || idx}
                  className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg"
                >
                  <div className="flex-shrink-0 mt-0.5">
                    <Calendar className="h-4 w-4 text-slate-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold" style={{ color: "#1F2937" }}>
                      {dl.title}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {dl.matterId?.title || ""}
                    </p>
                  </div>
                  <span className="text-xs text-slate-500 flex-shrink-0">
                    {formatDate(dl.dueAt)}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Sidebar column */}
      <div className="space-y-6">
        <AiSuggestionsWidget
          pendingCount={lawyerData.aiSuggestions?.pendingCount ?? 0}
          totalValueCents={lawyerData.aiSuggestions?.totalValueCents ?? 0}
          topSuggestions={lawyerData.aiSuggestions?.topSuggestions ?? []}
        />
        <ScheduleWidget events={core.todayEvents} />
        <MessagesWidget messages={core.recentMessages} />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Role Layout: Billing (2+1 grid)
// ---------------------------------------------------------------------------

const BillingDashboard: React.FC<{
  data: any;
  core: CoreData;
  onMarkDone: (taskId: string) => void;
}> = ({ data, core, onMarkDone }) => {
  const billingData = data?.billing || {};

  const arAgingData = (billingData.arAging || []).map((a: any) => ({
    label: a.bucket,
    totalCents: a.totalCents,
    count: a.count,
  }));

  const outstandingInvoices = billingData.outstandingInvoices || {
    totalBalanceDueCents: 0,
    count: 0,
  };

  const recentPayments: any[] = billingData.recentPayments || [];
  const trustBalances: any[] = billingData.trustBalances || [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Main column */}
      <div className="lg:col-span-2 space-y-6">
        {/* Top row: Gauge + 2 StatCards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <GaugeWidget
            label="Collection Rate"
            value={billingData.collectionRate ?? 0}
            color="#0d6efd"
            icon={DollarSign}
          />
          <StatCard
            label="Outstanding Invoices"
            value={`${outstandingInvoices.count} (${formatCurrency(outstandingInvoices.totalBalanceDueCents)})`}
            icon={CreditCard}
            color="#f59e0b"
          />
          <StatCard
            label="Unbilled WIP"
            value={formatCurrency(billingData.unbilledWipCents ?? 0)}
            icon={Wallet}
            color="#0F5132"
          />
        </div>

        <ArAgingChart data={arAgingData} />

        {/* Recent Payments (inline) */}
        <section className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <DollarSign className="h-5 w-5 text-slate-700" aria-hidden="true" />
            <h2 className="text-lg font-bold" style={{ color: "#1F2937" }}>
              Recent Payments
            </h2>
          </div>
          {recentPayments.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">No recent payments</p>
          ) : (
            <div className="space-y-3">
              {recentPayments.map((p: any, idx: number) => (
                <div
                  key={p._id || idx}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium" style={{ color: "#1F2937" }}>
                      {p.clientId?.displayName || p.clientName || "Client"}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {formatDate(p.paidAt || p.createdAt || "")}
                    </p>
                  </div>
                  <span className="text-sm font-bold" style={{ color: "#198754" }}>
                    {formatCurrency(p.amountCents ?? p.amount ?? 0)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Trust Balances (inline) */}
        <section className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Wallet className="h-5 w-5 text-slate-700" aria-hidden="true" />
            <h2 className="text-lg font-bold" style={{ color: "#1F2937" }}>
              Trust Balances
            </h2>
          </div>
          {trustBalances.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">No trust balances</p>
          ) : (
            <div className="space-y-3">
              {trustBalances.map((t: any, idx: number) => (
                <div
                  key={t.clientId || idx}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                >
                  <p className="text-sm font-medium" style={{ color: "#1F2937" }}>
                    {t.clientName || "Client"}
                  </p>
                  <span className="text-sm font-bold" style={{ color: "#0F5132" }}>
                    {formatCurrency(t.balanceCents ?? 0)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Sidebar column */}
      <div className="space-y-6">
        <FinancialAlertsWidget alerts={billingData.financialAlerts || []} />
        <TaskListWidget tasks={core.tasks} onMarkDone={onMarkDone} />
        <MessagesWidget messages={core.recentMessages} />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Role Layout: Staff (2+1 grid)
// ---------------------------------------------------------------------------

const StaffDashboard: React.FC<{
  data: any;
  core: CoreData;
  onMarkDone: (taskId: string) => void;
}> = ({ data, core, onMarkDone }) => {
  const staffData = data?.staff || {};
  const upcomingDeadlines: any[] = staffData.upcomingDeadlines || [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Main column */}
      <div className="lg:col-span-2 space-y-6">
        <TaskListWidget tasks={core.tasks} onMarkDone={onMarkDone} />

        <MatterListWidget
          matters={staffData.myMatters || []}
          title="My Matters"
        />

        {/* Upcoming Deadlines (inline) */}
        {upcomingDeadlines.length > 0 && (
          <section className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="h-5 w-5 text-slate-700" aria-hidden="true" />
              <h2 className="text-lg font-bold" style={{ color: "#1F2937" }}>
                Upcoming Deadlines
              </h2>
            </div>
            <div className="space-y-3">
              {upcomingDeadlines.map((dl: any, idx: number) => (
                <div
                  key={dl._id || idx}
                  className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg"
                >
                  <div className="flex-shrink-0 mt-0.5">
                    <Calendar className="h-4 w-4 text-slate-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold" style={{ color: "#1F2937" }}>
                      {dl.title}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {dl.matterId?.title || ""}
                    </p>
                  </div>
                  <span className="text-xs text-slate-500 flex-shrink-0">
                    {formatDate(dl.dueAt)}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Sidebar column */}
      <div className="space-y-6">
        <ScheduleWidget events={core.todayEvents} />
        <MessagesWidget messages={core.recentMessages} />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Dashboard Component
// ---------------------------------------------------------------------------

const Dashboard: React.FC<DashboardProps> = ({
  handleSignOut,
  customerId,
  priceId,
  subscriptionStatus,
  trialExpiresAt,
}) => {
  void customerId;
  void priceId;
  void subscriptionStatus;
  void trialExpiresAt;

  const navigate = useNavigate();
  const { userData, firmData } = useUserDataContext();
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const firmLoaded = Boolean(firmData?._id);
  const isOnTrial = firmData?.isOnTrial;
  const trialExpiration = firmData?.stripe?.trialExpiration;
  const daysLeft = trialExpiration
    ? Math.max(
        0,
        Math.ceil(
          (new Date(trialExpiration).getTime() - Date.now()) /
            (1000 * 60 * 60 * 24)
        )
      )
    : 0;
  const showBanner = firmLoaded && isOnTrial && !bannerDismissed;

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboard = useCallback(async () => {
    try {
      setError(null);
      const res = await dashboardApi.get();
      setData(res.data);
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load dashboard"
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const handleMarkDone = async (taskId: string) => {
    try {
      await tasksApi.changeStatus(taskId, "done");
      await fetchDashboard();
    } catch (err: any) {
      console.error("Failed to mark task as done", err);
    }
  };

  // Role from backend response
  const role: DashboardRole = data?.role || "staff";
  const userName = data?.userName || userData.name || "";

  // Core data shared across all roles
  const core: CoreData = data?.core || {
    tasks: { overdue: [], today: [], week: [] },
    todayEvents: [],
    recentMessages: [],
  };

  const badge = roleBadgeStyles[role];

  // ---- Loading state ----
  if (loading) {
    return (
      <ThemeProvider>
        <div
          className="min-h-screen w-full flex"
          style={{ backgroundColor: "#F4F6F8" }}
        >
          <Sidebar handleSignOut={handleSignOut} />
          <div className="flex-1 flex items-center justify-center">
            <div className="flex flex-col items-center gap-4">
              <div
                className="h-12 w-12 rounded-full border-4 border-slate-200 animate-spin"
                style={{ borderTopColor: "var(--color-primary)" }}
              />
              <p className="text-slate-500 font-medium">
                Loading dashboard...
              </p>
            </div>
          </div>
        </div>
      </ThemeProvider>
    );
  }

  // ---- Error state ----
  if (error) {
    return (
      <ThemeProvider>
        <div
          className="min-h-screen w-full flex"
          style={{ backgroundColor: "#F4F6F8" }}
        >
          <Sidebar handleSignOut={handleSignOut} />
          <div className="flex-1 flex items-center justify-center">
            <div className="flex flex-col items-center gap-4 text-center">
              <AlertCircle className="h-12 w-12 text-red-400" />
              <p className="text-slate-700 font-semibold">
                Something went wrong
              </p>
              <p className="text-sm text-slate-500">{error}</p>
              <button
                onClick={() => {
                  setLoading(true);
                  fetchDashboard();
                }}
                className="px-4 py-2 text-sm font-medium rounded-xl text-white"
                style={{ backgroundColor: "var(--color-primary)" }}
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </ThemeProvider>
    );
  }

  // ---- Main render ----
  return (
    <ThemeProvider>
      <div
        className="min-h-screen w-full flex"
        style={{ backgroundColor: "#F4F6F8" }}
      >
        <Sidebar handleSignOut={handleSignOut} />

        <div className="flex-1 overflow-auto">
          {/* Trial Banner */}
          {showBanner && (
            <div className="px-6 pt-4">
              <div
                className="rounded-xl px-5 py-3 flex items-center justify-between shadow-sm"
                style={{
                  background:
                    "linear-gradient(to right, var(--color-primary), var(--color-primary-hover))",
                }}
              >
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-lg bg-white/20 flex items-center justify-center">
                    <Sparkles className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">
                      You're on a free trial - {daysLeft} day
                      {daysLeft !== 1 ? "s" : ""} remaining
                    </p>
                    <p className="text-xs text-white/70">
                      Upgrade now to keep all your data and features
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => navigate("/settings/subscription")}
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-white rounded-lg text-sm font-bold transition-colors"
                    style={{ color: "var(--color-primary)" }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor =
                        "var(--color-primary-light)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "white";
                    }}
                  >
                    Upgrade Now
                    <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => setBannerDismissed(true)}
                    className="p-1.5 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="max-w-[1600px] mx-auto px-6 py-8">
            {/* Greeting Header */}
            <div className="mb-6">
              <h1
                className="text-2xl font-bold"
                style={{ color: "#1F2937" }}
              >
                Good {getTimeOfDay()}, {userName}
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                {new Date().toLocaleDateString("en-US", {
                  weekday: "long",
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}
                <span
                  className="ml-2 px-2 py-0.5 rounded-full text-xs font-medium"
                  style={{ backgroundColor: badge.bg, color: badge.text }}
                >
                  {badge.label}
                </span>
              </p>
            </div>

            {/* Role-based content */}
            {role === "owner" && (
              <OwnerDashboard
                data={data}
                core={core}
                onMarkDone={handleMarkDone}
              />
            )}
            {role === "lawyer" && (
              <LawyerDashboard
                data={data}
                core={core}
                onMarkDone={handleMarkDone}
              />
            )}
            {role === "billing" && (
              <BillingDashboard
                data={data}
                core={core}
                onMarkDone={handleMarkDone}
              />
            )}
            {role === "staff" && (
              <StaffDashboard
                data={data}
                core={core}
                onMarkDone={handleMarkDone}
              />
            )}
          </div>
        </div>
      </div>
    </ThemeProvider>
  );
};

export default Dashboard;
